import * as React from 'react';
import { useEffect, useState } from 'react';
import { Link, matchPath } from 'react-router-dom';
import * as fp from 'lodash/fp';

import { Profile } from 'generated/fetch';

import { BannerScenario } from 'app/lab/pages/workspace/initial-credits/banner-config';
import { CreditBanner } from 'app/lab/pages/workspace/initial-credits/credit-banner';
import { InvalidBillingBanner } from 'app/lab/pages/workspace/invalid-billing-banner';
import { dataTabPath, workspacePath } from 'app/routing/utils';
import colors from 'app/styles/colors';
import { withCurrentWorkspace } from 'app/utils';
import {
  MatchParams,
  profileStore,
  RouteDataStore,
  routeDataStore,
  serverConfigStore,
  withStore,
} from 'app/utils/stores';
import { WorkspaceData } from 'app/utils/workspace-data';
import { isUsingInitialCredits } from 'app/utils/workspace-utils';

import { BreadcrumbType } from './breadcrumb-type';

const styles = {
  firstLink: {
    color: colors.accent,
    textDecoration: 'none',
  },
  lastLink: {
    color: colors.primary,
    fontWeight: 600,
    fontSize: '1.5rem',
    textDecoration: 'none',
  },
};

class BreadcrumbData {
  label: string;
  url: string;

  constructor(label: string, url: string) {
    this.label = label;
    this.url = url;
  }
}

// Generates a trail of breadcrumbs based on currently loaded data.
export const getTrail = (
  type: BreadcrumbType,
  workspace: WorkspaceData,
  params: MatchParams
): Array<BreadcrumbData> => {
  const { ns, terraName } = params;
  switch (type) {
    case BreadcrumbType.Workspaces:
      return [new BreadcrumbData('Workspaces', '/workspaces')];
    case BreadcrumbType.Workspace:
      return [
        ...getTrail(BreadcrumbType.Workspaces, workspace, params),
        new BreadcrumbData(
          workspace ? workspace.name : '...',
          dataTabPath(ns, terraName)
        ),
      ];
    case BreadcrumbType.WorkspaceEdit:
      return [
        ...getTrail(BreadcrumbType.Workspace, workspace, params),
        new BreadcrumbData(
          'Edit Workspace',
          `${workspacePath(ns, terraName)}/edit`
        ),
      ];
    case BreadcrumbType.WorkspaceDuplicate:
      return [
        ...getTrail(BreadcrumbType.Workspace, workspace, params),
        new BreadcrumbData(
          'Duplicate Workspace',
          `${workspacePath(ns, terraName)}/duplicate`
        ),
      ];
    case BreadcrumbType.Data:
      return [
        ...getTrail(BreadcrumbType.Workspaces, workspace, params),
        new BreadcrumbData(
          workspace ? workspace.name : '...',
          `${dataTabPath(ns, terraName)}`
        ),
      ];
    default:
      return [];
  }
};

const BreadcrumbLink = ({ href, ...props }) => {
  return <Link to={href} {...props} />;
};

const shouldShowInvalidBillingBanner = (
  workspace: WorkspaceData,
  profile: Profile
) => {
  if (!workspace || !profile || !isUsingInitialCredits(workspace)) {
    return false;
  }

  const { creatorUser } = workspace;
  const isExpired = workspace.initialCredits.expirationEpochMillis < Date.now();
  const isExhausted = workspace.initialCredits.exhausted;

  return (
    creatorUser?.givenName &&
    creatorUser?.familyName &&
    (isExhausted || (!workspace.initialCredits.expirationBypassed && isExpired))
  );
};

const getCreditBannerData = (workspace: WorkspaceData, profile: Profile) => {
  if (!workspace || !profile) {
    return null;
  }

  const { initialCreditsLimit = 0, initialCreditsUsage = 0 } = profile;
  const {
    initialCredits: {
      exhausted,
      expirationBypassed,
      expirationEpochMillis,
      balance,
    },
  } = workspace;

  const usingInitialCreditsBillingAccount = isUsingInitialCredits(workspace);
  if (!usingInitialCreditsBillingAccount) {
    return null;
  }
  const now = Date.now();
  const givenName = workspace?.creatorUser?.givenName;
  const familyName = workspace?.creatorUser?.familyName;
  const creditBalance = balance ?? initialCreditsLimit - initialCreditsUsage;

  const scenarios = [
    {
      cond: exhausted,
      scenario: BannerScenario.Exhausted,
    },
    {
      cond: !expirationBypassed && expirationEpochMillis < now,
      scenario: BannerScenario.Expired,
    },
    {
      cond:
        !expirationBypassed &&
        expirationEpochMillis - now < 24 * 60 * 60 * 1000 * 90, // < 90 days
      scenario: BannerScenario.ExpiringSoon,
    },
    {
      cond: creditBalance <= 150, // $150 is 50% of $300 initial credits
      scenario: BannerScenario.LowBalance,
    },
  ];

  const matches = scenarios.filter((s) => s.cond).map((s) => s.scenario);
  if (matches.length === 0 || !givenName || !familyName) {
    return null;
  }

  return matches.map((match) => ({
    scenario: match,
    expirationDate: new Date(expirationEpochMillis).toLocaleDateString(
      'en-US',
      {
        weekday: 'short', // e.g., 'Mon'
        year: 'numeric', // e.g., '2025'
        month: 'short', // e.g., 'Jan'
        day: '2-digit', // e.g., '01'
      }
    ), // e.g., 'Mon, Jan 01, 2025'
    creatorName: `${givenName} ${familyName}`.trim(),
    creditBalance: creditBalance.toFixed(2),
    workspace,
    profile,
  }));
};

interface Props {
  workspace: WorkspaceData;
  routeData: RouteDataStore;
}

export const Breadcrumb = fp.flow(
  withCurrentWorkspace(),
  withStore(routeDataStore, 'routeData')
)((props: Props) => {
  const { profile } = profileStore.get();
  const [showInvalidBillingBanner, setShowInvalidBillingBanner] = useState(
    shouldShowInvalidBillingBanner(props.workspace, profile)
  );
  const [creditBannerData, setCreditBannerData] = useState(
    getCreditBannerData(props.workspace, profile)
  );

  // Use feature flag to render initial credits banners
  const { enableUnlinkBillingForInitialCredits = false } =
    serverConfigStore.get().config;

  useEffect(() => {
    // When user navigates to a different workspace, show the credit banner even if dismissed in the past
    setShowInvalidBillingBanner(
      shouldShowInvalidBillingBanner(props.workspace, profile)
    );
    setCreditBannerData(getCreditBannerData(props.workspace, profile));
  }, [props?.workspace, profile]);

  const trail = (): Array<BreadcrumbData> => {
    const workspaceMatch = matchPath<MatchParams>(location.pathname, {
      path: '/workspaces/:ns/:terraName',
    });
    const { ns = '', terraName = '' } = workspaceMatch
      ? workspaceMatch.params
      : {};

    return getTrail(props.routeData.breadcrumb, props.workspace, {
      ns,
      terraName,
    });
  };

  const first = (): Array<BreadcrumbData> => {
    return fp.dropRight(1, trail());
  };

  const last = (): BreadcrumbData => {
    return fp.last(trail());
  };

  const handleBannerClose = (idx: number) => {
    setCreditBannerData((prev) =>
      prev ? prev.filter((_, i) => i !== idx) : null
    );
  };

  return (
    <>
      {!enableUnlinkBillingForInitialCredits && showInvalidBillingBanner && (
        <InvalidBillingBanner
          profile={profile}
          workspace={props.workspace}
          onClose={() => setShowInvalidBillingBanner(false)}
        />
      )}
      {enableUnlinkBillingForInitialCredits && creditBannerData && (
        <CreditBanner
          banners={creditBannerData.map((data, idx) => ({
            ...data,
            onClose: () => handleBannerClose(idx),
          }))}
        />
      )}
      <div
        style={{
          marginLeft: '4.875rem',
          display: 'inline-block',
        }}
      >
        {first().map(({ label, url }, i) => {
          return (
            <React.Fragment key={i}>
              <BreadcrumbLink href={url} style={styles.firstLink}>
                {label}
              </BreadcrumbLink>
              <span
                style={{
                  color: colors.primary,
                }}
              >
                {' '}
                &gt;{' '}
              </span>
            </React.Fragment>
          );
        })}
        {last() && (
          <div>
            <BreadcrumbLink href={last().url} style={styles.lastLink}>
              {last().label}
            </BreadcrumbLink>
          </div>
        )}
      </div>
    </>
  );
});
